import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  CircleAlert,
  Info,
  Link2,
  Search,
  Sparkles,
  Unlink,
  X,
} from "lucide-react";
import { httpClient } from "../../HttpClient";
import { dialog } from "../../components/ui/Dialog";
import { showStatusToast } from "../../components/ui/Toast";
import { fetchClientScope } from "./useClients.js";
import { useEtatGlobal } from "../EtatGlobal.js";

// Liaison de la fiche client avec son dossier ACD (i-Suite Expert) — demande
// d'Anthony (10/2026). Le collaborateur cherche et choisit lui-même le dossier
// ACD ; à la validation, le code ACD est enregistré (champ non modifiable
// ailleurs) et les informations ACD cochées sont reprises dans la fiche.
// Backend : controllers/acdController.js (lecture seule chez ACD).

const ACD_API = "https://backend-myalfa.vercel.app/api/acd";
const ROLES_LIAISON = ["ADMINISTRATEUR", "EXPERT_COMPTABLE"];

// Collaborateur Niv. 1 du dossier (fiche ou équipe d'une année) : peut aussi
// lier, délier et récupérer les FEC (demande de Jonathan du 20/10/2026).
// Même règle que le backend (acdController.estCollabNiv1).
const minuscule = (v) => String(v || "").trim().toLowerCase();
export const estCollabNiv1 = (client, utilisateur) => {
  const email = minuscule(utilisateur?.email);
  if (!email || !client) return false;
  return [client.email_collaborateur, ...(client.equipeAnnees || []).map((e) => e.email_collaborateur)].some(
    (e) => minuscule(e) === email,
  );
};

// Champs de la fiche que la liaison peut modifier (code ACD + informations
// reprises d'ACD) — pour les écrans qui gardent leur propre copie du client
// (CRM du Suivi Bilan).
export const CHAMPS_LIAISON = [
  "codeACD", "codeACDLieLe", "codeACDLiePar",
  "nom", "siret", "siren", "formeSociale", "adresse", "codePostal", "ville", "telephone", "email",
  "codeNAF", "activite", "tvaIntracommunautaire", "regimeFiscal", "regimeTVA", "dateDebutMission",
];

const dateFr = (iso) => (iso ? String(iso).slice(0, 10).split("-").reverse().join("/") : null);
const messageErreur = (err, defaut) => err?.response?.data?.message || defaut;

// Valeurs de la fiche après reprise des champs cochés (affichage instantané,
// remplacé par la réponse du serveur).
const majLocale = (comparaison, coches) => {
  const data = {};
  comparaison
    .filter((x) => x.proposable && coches.includes(x.cle))
    .forEach((x) => {
      if (x.cle === "formeSociale") data.formeSociale = [x.acd];
      else if (x.cle === "dateDebutMission") data.dateDebutMission = `${x.acd}T00:00:00.000Z`;
      else data[x.cle] = x.acd;
    });
  return data;
};

function DejaLie({ noms }) {
  if (!noms?.length) return null;
  return (
    <p className="mt-1 flex items-center gap-1 text-[11px] text-amber-700">
      <CircleAlert className="h-3 w-3 shrink-0" aria-hidden="true" />
      Déjà lié à : {noms.join(", ")}
    </p>
  );
}

function LigneDossier({ d, onLier }) {
  return (
    <li className="flex items-start justify-between gap-3 rounded-lg border border-slate-200 px-3 py-2.5 hover:border-[#840040]/30 hover:bg-[#840040]/[0.02]">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-xs font-semibold text-slate-900">{d.nom || "(sans nom)"}</span>
          {d.motif && (
            <span className="rounded-full bg-[#840040]/10 px-1.5 py-0.5 text-[10px] font-semibold text-[#840040]">
              {d.motif}
            </span>
          )}
          {d.sorti && (
            <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-500">
              Sorti
            </span>
          )}
        </div>
        <p className="mt-0.5 text-[11px] text-slate-500">
          <span className="font-mono text-slate-700">{d.code}</span>
          {[d.forme, d.siret && `SIRET ${d.siret}`, d.ville, d.cabinet]
            .filter(Boolean)
            .map((x) => ` · ${x}`)
            .join("")}
        </p>
        <DejaLie noms={d.dejaLieA} />
      </div>
      <button
        type="button"
        onClick={() => onLier(d)}
        className="shrink-0 rounded-md bg-[#840040] px-3 py-1 text-[11px] font-semibold text-white transition-colors hover:bg-[#6d0035] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#840040]/40"
      >
        Lier
      </button>
    </li>
  );
}

// Squelettes de chargement à la forme des vrais contenus (pas de spinner).
// Pulsation en opacité, volontairement toujours active (aucun mouvement) pour
// qu'on voie qu'il s'agit d'un chargement ; décalée d'une ligne à l'autre.
const PULSE = "animate-[pulse_1.2s_ease-in-out_infinite]";
const decalage = (i) => ({ animationDelay: `${i * 0.15}s` });
const Barre = ({ className }) => <span className={`block rounded bg-slate-300/70 ${className}`} />;

function SqueletteDossiers({ lignes = 3 }) {
  return (
    <ul className="space-y-2" aria-hidden="true">
      {Array.from({ length: lignes }, (_, i) => (
        <li key={i} style={decalage(i)} className={`flex items-center justify-between gap-3 rounded-lg border border-slate-100 px-3 py-3 ${PULSE}`}>
          <div className="min-w-0 flex-1 space-y-1.5">
            <div className="flex items-center gap-1.5">
              <Barre className={`h-3 ${["w-44", "w-36", "w-52"][i % 3]}`} />
              {i === 0 && <Barre className="h-3 w-20 rounded-full bg-[#840040]/15" />}
            </div>
            <Barre className={`h-2.5 ${["w-64", "w-56", "w-48"][i % 3]}`} />
          </div>
          <Barre className="h-6 w-12 rounded-md" />
        </li>
      ))}
    </ul>
  );
}

function SqueletteApercu() {
  return (
    <div className={`space-y-4 ${PULSE}`} aria-hidden="true">
      <div className="space-y-2 rounded-lg border border-slate-100 bg-slate-50/60 p-3">
        <div className="flex items-center gap-2">
          <Barre className="h-4 w-48" />
          <Barre className="h-4 w-20" />
        </div>
        {["w-40", "w-56", "w-72", "w-52", "w-44"].map((w) => (
          <div key={w} className="flex gap-3">
            <Barre className="h-2.5 w-24" />
            <Barre className={`h-2.5 ${w}`} />
          </div>
        ))}
      </div>
      <div className="space-y-2 rounded-lg border border-slate-100 p-3">
        {[0, 1, 2, 3, 4].map((i) => (
          <div key={i} className="flex items-center gap-3">
            <Barre className="h-3.5 w-3.5" />
            <Barre className="h-2.5 w-28" />
            <Barre className="h-2.5 w-32" />
            <Barre className="h-2.5 w-36" />
          </div>
        ))}
      </div>
    </div>
  );
}

// Attente des suggestions : bandeau engageant + squelette des dossiers.
function AttenteSuggestions({ client, lent }) {
  const siren = String(client.siren || client.siret || "").replace(/D/g, "").slice(0, 9);
  const criteres = [siren.length === 9 && `SIREN ${siren}`, client.nom && `« ${client.nom.trim()} »`].filter(Boolean);
  return (
    <div className="space-y-3">
      <div className="flex gap-3 rounded-lg border border-[#840040]/15 bg-gradient-to-r from-[#840040]/[0.07] to-[#840040]/[0.01] p-3">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#840040]/10">
          <Sparkles className={`h-4 w-4 text-[#840040] ${PULSE}`} aria-hidden="true" />
        </span>
        <div className="min-w-0" role="status">
          <p className="text-xs font-semibold text-[#840040]">Patientez un instant…</p>
          <p className="mt-0.5 text-xs text-slate-600">
            Nous vous suggérons les dossiers ACD qui correspondent aux informations de ce client.
          </p>
          {criteres.length > 0 && (
            <p className="mt-1.5 flex flex-wrap gap-1">
              {criteres.map((c) => (
                <span key={c} className="rounded-full bg-white px-2 py-0.5 text-[10px] font-medium text-slate-600 ring-1 ring-slate-200">
                  {c}
                </span>
              ))}
            </p>
          )}
          {lent && (
            <p className="mt-1.5 text-[11px] text-slate-400">
              Première recherche du jour : chargement de la liste des dossiers ACD.
            </p>
          )}
        </div>
      </div>
      <SqueletteDossiers />
    </div>
  );
}

// Étape 1 : barre de recherche + suggestions (SIREN, nom).
function Recherche({ client, onChoisir }) {
  const [q, setQ] = useState("");
  const [suggestions, setSuggestions] = useState(null);
  const [erreurSuggestions, setErreurSuggestions] = useState("");
  const [resultats, setResultats] = useState(null);
  const [recherche, setRecherche] = useState(false);
  const [lent, setLent] = useState(false);
  const derniere = useRef(0);

  useEffect(() => {
    let actif = true;
    // La première ouverture peut attendre le chargement de la liste ACD.
    const minuterie = setTimeout(() => actif && setLent(true), 3000);
    httpClient
      .get(`${ACD_API}/clients/${client.id}/suggestions`)
      .then(({ data }) => actif && setSuggestions(data?.suggestions || []))
      .catch((err) => {
        if (!actif) return;
        setSuggestions([]);
        setErreurSuggestions(messageErreur(err, "Suggestions indisponibles."));
      })
      .finally(() => clearTimeout(minuterie));
    return () => {
      actif = false;
      clearTimeout(minuterie);
    };
  }, [client.id]);

  useEffect(() => {
    const texte = q.trim();
    if (texte.length < 2) {
      setResultats(null);
      setRecherche(false);
      return undefined;
    }
    setRecherche(true);
    const id = ++derniere.current;
    const minuterie = setTimeout(() => {
      httpClient
        .get(`${ACD_API}/dossiers`, { params: { q: texte, clientId: client.id } })
        .then(({ data }) => id === derniere.current && setResultats(data?.resultats || []))
        .catch(() => id === derniere.current && setResultats([]))
        .finally(() => id === derniere.current && setRecherche(false));
    }, 250);
    return () => clearTimeout(minuterie);
  }, [q, client.id]);

  const enRecherche = q.trim().length >= 2;

  return (
    <>
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" aria-hidden="true" />
        <input
          id="acd-recherche"
          type="search"
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Rechercher par nom, code ACD ou SIRET…"
          className="w-full rounded-lg border border-slate-200 py-2 pl-8 pr-3 text-xs text-slate-700 focus:outline-none focus:ring-1 focus:ring-[#840040]/40"
        />
      </div>

      <div className="mt-3 min-h-[180px]">
        {enRecherche ? (
          recherche && !resultats?.length ? (
            <SqueletteDossiers />
          ) : resultats?.length ? (
            <ul className="space-y-2">
              {resultats.map((d) => (
                <LigneDossier key={d.code} d={d} onLier={onChoisir} />
              ))}
            </ul>
          ) : (
            <p className="py-10 text-center text-xs text-slate-500">
              Aucun dossier ACD ne correspond à « {q.trim()} ».
            </p>
          )
        ) : suggestions === null ? (
          <AttenteSuggestions client={client} lent={lent} />
        ) : suggestions.length ? (
          <>
            <div className="mb-2 flex items-center gap-1.5">
              <Sparkles className="h-3.5 w-3.5 text-[#840040]" aria-hidden="true" />
              <p className="text-xs font-semibold text-[#840040]">Voici les correspondances possibles</p>
              <span className="text-[11px] text-slate-400">· vérifiez puis cliquez sur « Lier »</span>
            </div>
            <ul className="space-y-2">
              {suggestions.map((d) => (
                <LigneDossier key={d.code} d={d} onLier={onChoisir} />
              ))}
            </ul>
          </>
        ) : (
          <div className="py-10 text-center text-xs text-slate-500">
            <p className="font-medium text-slate-600">Aucun dossier suggéré</p>
            <p className="mt-1 text-[11px] text-slate-400">
              {erreurSuggestions || "Recherchez le dossier par son nom, son code ACD ou son SIRET."}
            </p>
          </div>
        )}
      </div>
    </>
  );
}

function Detail({ libelle, valeur }) {
  if (!valeur) return null;
  return (
    <div className="flex gap-3 text-xs">
      <span className="w-28 shrink-0 text-slate-500">{libelle}</span>
      <span className="min-w-0 font-medium text-slate-900">{valeur}</span>
    </div>
  );
}

// Étape 2 : « Voulez-vous lier ce dossier ? » + reprise champ par champ.
function Confirmation({ apercu, coches, setCoches }) {
  const { dossier, comparaison, dejaLieA, depuisCopie } = apercu;
  const lignes = comparaison.filter((x) => x.acd !== null);
  const proposables = lignes.filter((x) => x.proposable);
  const basculer = (cle) =>
    setCoches((prev) => (prev.includes(cle) ? prev.filter((c) => c !== cle) : [...prev, cle]));

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-3">
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <span className="text-sm font-semibold text-slate-900">{dossier.nom}</span>
          <span className="rounded bg-white px-1.5 py-0.5 font-mono text-[11px] text-slate-700 ring-1 ring-slate-200">
            {dossier.code}
          </span>
          {dossier.dateSortie && (
            <span className="rounded-full bg-slate-200 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600">
              Sorti le {dateFr(dossier.dateSortie)}
            </span>
          )}
        </div>
        <div className="space-y-1">
          <Detail libelle="Forme" valeur={dossier.forme} />
          <Detail libelle="SIRET" valeur={dossier.siret} />
          <Detail libelle="Adresse" valeur={dossier.adresse} />
          <Detail libelle="Activité" valeur={dossier.activite} />
          <Detail libelle="Régimes" valeur={dossier.regimes} />
          <Detail libelle="Cabinet" valeur={dossier.cabinet} />
          <Detail libelle="Collaborateurs" valeur={dossier.collaborateurs?.join(", ")} />
          <Detail libelle="Date d'entrée" valeur={dateFr(dossier.dateEntree)} />
          <Detail libelle="Contact" valeur={[dossier.email, dossier.telephone].filter(Boolean).join(" · ")} />
        </div>
        <DejaLie noms={dejaLieA} />
        {depuisCopie && (
          <p className="mt-2 flex items-center gap-1 text-[11px] text-slate-500">
            <Info className="h-3 w-3 shrink-0" aria-hidden="true" />
            ACD ne répond pas : informations issues de la copie du jour.
          </p>
        )}
      </div>

      <div>
        <div className="mb-2 flex items-baseline justify-between gap-2">
          <h4 className="text-xs font-semibold text-slate-900">Informations à reprendre dans la fiche</h4>
          <span className="text-[11px] text-slate-500">
            {proposables.length
              ? `${coches.length} champ${coches.length > 1 ? "s" : ""} mis à jour`
              : "Fiche déjà à jour"}
          </span>
        </div>
        <div className="overflow-x-auto rounded-lg border border-slate-200">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-[11px] text-slate-500">
              <tr>
                <th className="w-8 px-2 py-1.5" aria-label="Reprendre" />
                <th className="px-2 py-1.5 text-left font-medium">Champ</th>
                <th className="px-2 py-1.5 text-left font-medium">Avant (fiche)</th>
                <th className="px-2 py-1.5 text-left font-medium">ACD</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {lignes.map((x) => {
                const coche = x.proposable && coches.includes(x.cle);
                return (
                  <tr key={x.cle} className={coche ? "bg-emerald-50/50" : ""}>
                    <td className="px-2 py-1.5 text-center">
                      {x.proposable && (
                        <input
                          id={`acd-champ-${x.cle}`}
                          type="checkbox"
                          checked={coche}
                          onChange={() => basculer(x.cle)}
                          className="h-3.5 w-3.5 accent-[#840040]"
                        />
                      )}
                    </td>
                    <td className="px-2 py-1.5 text-slate-600">
                      <label htmlFor={x.proposable ? `acd-champ-${x.cle}` : undefined}>{x.libelle}</label>
                    </td>
                    <td className={`px-2 py-1.5 ${coche ? "text-slate-400 line-through" : "text-slate-700"}`}>
                      {x.fiche ?? <span className="italic text-slate-400">vide</span>}
                    </td>
                    <td className="px-2 py-1.5">
                      {x.identique ? (
                        <span className="text-slate-400">Identique</span>
                      ) : (
                        <span className={coche ? "font-semibold text-emerald-700" : "text-slate-700"}>{x.acd}</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="mt-1.5 text-[11px] text-slate-400">
          Rien n'est modifié dans ACD. Seules les informations cochées sont reportées dans la fiche.
        </p>
      </div>
    </div>
  );
}

// Utilisable ailleurs que dans la fiche : `rolesLiaison` restreint qui peut
// lier (CRM : Administrateurs ; le Collaborateur Niv. 1 du dossier le peut
// partout), `onLie` est appelé une fois la liaison
// enregistrée par le serveur, la ref expose ouvrir() pour ouvrir la fenêtre
// depuis un autre bouton, `nonLie` remplace le « - » affiché à ceux qui ne
// peuvent pas lier.
const LiaisonAcd = forwardRef(function LiaisonAcd(
  { client, utilisateur, onClientUpdated, rolesLiaison = ROLES_LIAISON, onLie, nonLie = "-" },
  ref,
) {
  const [ouvert, setOuvert] = useState(false);
  const [choix, setChoix] = useState(null); // dossier choisi dans la liste
  const [apercu, setApercu] = useState(null);
  const [erreurApercu, setErreurApercu] = useState("");
  const [coches, setCoches] = useState([]);
  const queryClient = useQueryClient();
  const { patchSuiviBilanClient } = useEtatGlobal();
  const clientRef = useRef(client);
  clientRef.current = client;
  const nomUtilisateur = [utilisateur?.name, utilisateur?.lastName].filter(Boolean).join(" ") || "Utilisateur MyALFA";

  const collabNiv1 = estCollabNiv1(client, utilisateur);
  const peutLier = rolesLiaison.includes(utilisateur?.role) || collabNiv1;
  const peutDelier = utilisateur?.role === "ADMINISTRATEUR" || collabNiv1;

  useImperativeHandle(ref, () => ({ ouvrir: () => setOuvert(true) }), []);

  const choixCourant = useRef(null);
  const fermer = useCallback(() => {
    choixCourant.current = null;
    setOuvert(false);
    setChoix(null);
    setApercu(null);
    setErreurApercu("");
  }, []);

  useEffect(() => {
    if (!ouvert) return undefined;
    const echap = (e) => e.key === "Escape" && fermer();
    window.addEventListener("keydown", echap);
    return () => window.removeEventListener("keydown", echap);
  }, [ouvert, fermer]);

  const choisir = (d) => {
    choixCourant.current = d.code;
    setChoix(d);
    setApercu(null);
    setErreurApercu("");
    httpClient
      .get(`${ACD_API}/clients/${client.id}/dossiers/${encodeURIComponent(d.code)}`)
      .then(({ data }) => {
        if (choixCourant.current !== d.code) return;
        setApercu(data);
        setCoches(data.comparaison.filter((x) => x.proposable && x.cocheParDefaut).map((x) => x.cle));
      })
      .catch((err) => choixCourant.current === d.code && setErreurApercu(messageErreur(err, "Lecture du dossier ACD impossible.")));
  };

  // Tout est appliqué à l'écran immédiatement, dans le cache partagé (fiche,
  // liste, formulaire Modifier qui relit la fiche) et chez l'écran parent ;
  // l'enregistrement suit en arrière-plan. En cas d'échec, seuls les champs
  // touchés par la liaison reprennent leur valeur d'avant (une autre
  // modification faite entre-temps est conservée).
  const appliquer = (modif) => {
    const id = clientRef.current.id;
    const avecTimeline = (c) => {
      const suivant = { ...c, ...modif.champs };
      if (Array.isArray(c.historiques) && modif.historiques) suivant.historiques = modif.historiques(c.historiques);
      return suivant;
    };
    queryClient.setQueryData(["client", id], (prev) => (prev ? avecTimeline(prev) : prev));
    queryClient.setQueryData(["clients"], (prev) =>
      Array.isArray(prev) ? prev.map((c) => (c.id === id ? { ...c, ...modif.champs } : c)) : prev,
    );
    // Tableau Suivi Bilan (et CRM ouvert) : mis à jour tout de suite, quel
    // que soit l'écran d'où la liaison est faite.
    const champsSuivi = Object.fromEntries(Object.entries(modif.champs || {}).filter(([k]) => CHAMPS_LIAISON.includes(k)));
    if (Object.keys(champsSuivi).length) patchSuiviBilanClient?.(id, champsSuivi);
    const suivant = avecTimeline(clientRef.current);
    clientRef.current = suivant;
    onClientUpdated?.(suivant);
  };
  const retablir = (avant, cles, idTemporaire) =>
    appliquer({
      champs: Object.fromEntries(cles.map((k) => [k, avant[k] ?? null])),
      historiques: (h) => h.filter((x) => x.id !== idTemporaire),
    });

  // Ligne de Timeline affichée tout de suite, remplacée par celle du serveur.
  const ligneTemporaire = (titre, description, lignes) => ({
    id: `acd-${Date.now()}`,
    titre,
    description,
    source: "liaison_acd",
    auteur: nomUtilisateur,
    details: { lignes },
    createdAt: new Date().toISOString(),
  });
  const rafraichirTimeline = () =>
    fetchClientScope(clientRef.current.id, "timeline")
      .then((t) => Array.isArray(t?.historiques) && appliquer({ champs: {}, historiques: () => t.historiques }))
      .catch(() => {});

  const valider = async () => {
    const avant = clientRef.current;
    const { dossier, comparaison } = apercu;
    const champs = coches.filter((c) => comparaison.some((x) => x.cle === c && x.proposable));
    const reprises = majLocale(comparaison, champs);
    const repris = comparaison.filter((x) => champs.includes(x.cle));
    const temporaire = ligneTemporaire(
      `Liaison ACD : dossier ${dossier.code}`,
      `Fiche liée au dossier ACD ${dossier.code} (${dossier.nom}) par ${nomUtilisateur}${repris.length ? `, ${repris.length} champ${repris.length > 1 ? "s" : ""} mis à jour depuis ACD` : ", aucun champ modifié"}.`,
      [
        { libelle: "Dossier ACD", valeur: `${dossier.code} — ${dossier.nom}` },
        ...repris.map((x) => ({ libelle: x.libelle, valeur: `${x.fiche ?? "(vide)"} → ${x.acd}` })),
      ],
    );
    const modifies = { ...reprises, codeACD: dossier.code, codeACDLieLe: new Date().toISOString(), codeACDLiePar: nomUtilisateur };
    appliquer({ champs: modifies, historiques: (h) => [temporaire, ...h] });
    fermer();
    const n = champs.length;
    showStatusToast({
      status: "success",
      titre: "Dossier lié avec ACD",
      message: `${avant.nom} est lié au dossier ACD ${dossier.code}${n ? ` · ${n} information${n > 1 ? "s" : ""} mise${n > 1 ? "s" : ""} à jour` : ""}.`,
    });
    try {
      const { data } = await httpClient.post(`${ACD_API}/clients/${avant.id}/liaison`, { code: dossier.code, champs });
      // Valeurs du serveur (format des dates…) puis vraie ligne de Timeline.
      const serveur = Object.fromEntries(Object.keys(modifies).map((k) => [k, data.client?.[k] ?? null]));
      appliquer({ champs: serveur });
      rafraichirTimeline();
      onLie?.(dossier.code);
    } catch (err) {
      retablir(avant, Object.keys(modifies), temporaire.id);
      showStatusToast({
        status: "error",
        titre: "Liaison ACD non enregistrée",
        message: `${messageErreur(err, "La liaison avec ACD a échoué.")} Les informations de la fiche ont été rétablies.`,
      });
    }
  };

  const delier = async () => {
    const ok = await dialog.confirm(
      `Retirer la liaison avec le dossier ACD ${client.codeACD} ? Les informations de la fiche ne sont pas modifiées.`,
      { title: "Retirer la liaison ACD", variant: "danger", confirmLabel: "Retirer" },
    );
    if (!ok) return;
    const avant = clientRef.current;
    const temporaire = ligneTemporaire(
      `Liaison ACD retirée : dossier ${avant.codeACD}`,
      `Liaison avec le dossier ACD ${avant.codeACD} retirée par ${nomUtilisateur}. Les informations de la fiche ne sont pas modifiées.`,
      [{ libelle: "Ancien dossier ACD", valeur: avant.codeACD }],
    );
    const cles = ["codeACD", "codeACDLieLe", "codeACDLiePar"];
    appliquer({ champs: Object.fromEntries(cles.map((k) => [k, null])), historiques: (h) => [temporaire, ...h] });
    showStatusToast({ status: "success", titre: "Liaison ACD retirée", message: `${avant.nom} n'est plus lié au dossier ${avant.codeACD}.` });
    try {
      await httpClient.delete(`${ACD_API}/clients/${avant.id}/liaison`);
      rafraichirTimeline();
    } catch (err) {
      retablir(avant, cles, temporaire.id);
      showStatusToast({
        status: "error",
        titre: "Liaison ACD conservée",
        message: `${messageErreur(err, "Le retrait de la liaison a échoué.")} La liaison a été rétablie.`,
      });
    }
  };

  const infoLiaison = useMemo(
    () =>
      [client.codeACDLieLe && `Lié le ${new Date(client.codeACDLieLe).toLocaleDateString("fr-FR")}`, client.codeACDLiePar && `par ${client.codeACDLiePar}`]
        .filter(Boolean)
        .join(" "),
    [client.codeACDLieLe, client.codeACDLiePar],
  );

  if (client.codeACD) {
    return (
      // min-w-0 en cascade : un code ACD long est tronqué (« … »), complet au survol.
      <span className="flex min-w-0 items-center justify-end gap-1.5">
        <span
          title={infoLiaison || undefined}
          className="inline-flex shrink-0 items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700"
        >
          <Link2 className="h-3 w-3" aria-hidden="true" />
          Lié avec ACD
        </span>
        <span
          title={`Code ACD : ${client.codeACD}`}
          className="inline-flex min-w-0 items-center gap-1 rounded-md bg-slate-100 px-1.5 py-0.5 text-[11px] text-slate-500"
        >
          <span className="shrink-0">Code</span>
          <span className="min-w-0 truncate font-mono font-semibold text-slate-900">{client.codeACD}</span>
        </span>
        {peutDelier && (
          <button
            type="button"
            onClick={delier}
            title="Retirer la liaison ACD"
            aria-label="Retirer la liaison ACD"
            className="shrink-0 rounded p-0.5 text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-300"
          >
            <Unlink className="h-3.5 w-3.5" />
          </button>
        )}
      </span>
    );
  }

  if (!peutLier) return typeof nonLie === "string" ? <span className="font-medium text-slate-900">{nonLie}</span> : nonLie;

  return (
    <>
      <button
        type="button"
        onClick={() => setOuvert(true)}
        className="inline-flex items-center gap-1 rounded-md border border-[#840040]/30 bg-[#840040]/5 px-2 py-1 text-[11px] font-semibold text-[#840040] transition-colors hover:bg-[#840040]/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#840040]/40"
      >
        <Link2 className="h-3 w-3" aria-hidden="true" />
        À lier avec un dossier ACD
      </button>

      {ouvert &&
        createPortal(
          <div
            className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/30 p-4"
            onMouseDown={(e) => e.target === e.currentTarget && fermer()}
          >
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby="acd-titre"
              className="flex max-h-[85vh] w-full max-w-[680px] flex-col rounded-xl border border-slate-200 bg-white shadow-xl"
            >
              <div className="flex items-start justify-between gap-3 border-b border-slate-100 px-5 py-4">
                <div className="min-w-0">
                  <h3 id="acd-titre" className="flex items-center gap-1.5 text-sm font-bold text-slate-900">
                    {choix && (
                      <button
                        type="button"
                        onClick={() => {
                          choixCourant.current = null;
                          setChoix(null);
                        }}
                        aria-label="Retour à la recherche"
                        className="-ml-1 rounded p-0.5 text-slate-400 hover:text-slate-700"
                      >
                        <ArrowLeft className="h-4 w-4" />
                      </button>
                    )}
                    {choix ? "Voulez-vous lier ce dossier ?" : "Lier avec un dossier ACD"}
                  </h3>
                  <p className="mt-0.5 truncate text-[11px] text-slate-500">Fiche MyALFA : {client.nom}</p>
                </div>
                <button type="button" onClick={fermer} aria-label="Fermer">
                  <X className="h-4 w-4 text-slate-400 hover:text-slate-700" />
                </button>
              </div>

              <div className="overflow-y-auto px-5 py-4">
                {/* La recherche reste montée : le retour garde la saisie et les suggestions. */}
                <div className={choix ? "hidden" : undefined}>
                  <Recherche client={client} onChoisir={choisir} />
                </div>
                {!choix ? null : erreurApercu ? (
                  <p className="py-10 text-center text-xs text-red-600">{erreurApercu}</p>
                ) : !apercu ? (
                  <SqueletteApercu />
                ) : (
                  <Confirmation apercu={apercu} coches={coches} setCoches={setCoches} />
                )}
              </div>

              {choix && (
                <div className="flex justify-end gap-2 border-t border-slate-100 px-5 py-3">
                  <button
                    type="button"
                    onClick={fermer}
                    className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs text-slate-600 transition-colors hover:bg-slate-50"
                  >
                    Annuler
                  </button>
                  <button
                    type="button"
                    onClick={valider}
                    disabled={!apercu}
                    className="rounded-lg bg-[#840040] px-4 py-1.5 text-xs font-medium text-white transition-colors hover:bg-[#6d0035] disabled:opacity-60"
                  >
                    Valider la liaison
                  </button>
                </div>
              )}
            </div>
          </div>,
          document.body,
        )}
    </>
  );
});

export default LiaisonAcd;
